/**
 * User Management — the site-user (customer) console.
 *
 * Migrated from the legacy app-admin "Admin Operations" page, which exposed
 * the same upstream `/admin/*` endpoints as a flat dropdown of 14 operations
 * that each re-asked for the email. Here the email is looked up once and every
 * operation acts on the loaded account.
 *
 * NOT to be confused with `admin-user.ts`, which models admin-panel logins.
 */

// ── Domain model (adapter output — never the raw upstream DTO) ──────────

export interface UserAddOn {
  name: string;
  creationDate: string | null;
  /**
   * Upstream returns this and the legacy adapter dropped it, so the old panel
   * could not tell a live add-on from an expired one. `null` means the upstream
   * payload omitted it entirely.
   */
  isActive: boolean | null;
}

export interface UserPlanHistoryEntry {
  productPlanName: string;
  reason: string;
  startDate: string | null;
  endDate: string | null;
}

export interface UserEmailHistoryEntry {
  email: string;
  isCurrent: boolean;
  status: string;
  creationDate: string | null;
  updatedDate: string | null;
}

export interface UserActiveSubscription {
  subscriptionId: string | null;
  name: string;
  reason: string | null;
  paymentProvider: string | null;
  expiry: string | null;
  createdDate: string | null;
  startDate: string | null;
  nextChargeDate: string | null;
  nextChargeAmount: number | null;
  currency: string | null;
  frequencyUnit: string | null;
  frequencyLength: number | null;
  subscriptionSource: string | null;
  lastUpdatedOn: string | null;
}

export interface QuantRanksSummary {
  subscriptionCount: number;
  registrationDate: string | null;
  status: string | null;
}

/** Everything the console knows about one site user, from one lookup. */
export interface UserAccount {
  email: string;
  uid: string;
  tag: string | null;
  productPlanName: string | null;
  expiration: string | null;
  registrationDate: string | null;
  origin: string | null;
  lastChargePaymentProvider: string | null;
  nextChargePaymentProvider: string | null;
  addOns: UserAddOn[];
  planHistory: UserPlanHistoryEntry[];
  emailHistory: UserEmailHistoryEntry[];
  activeSubscriptions: UserActiveSubscription[];
  quantRanks: QuantRanksSummary | null;
}

// ── Billing (live provider data, read-only in this pass) ────────────────

export const BILLING_PROVIDERS = [
  "fastspring",
  "stripe",
  "bluesnap",
  "apple",
  "google",
] as const;
export type BillingProvider = (typeof BILLING_PROVIDERS)[number];

export const BILLING_PROVIDER_LABELS: Record<BillingProvider, string> = {
  fastspring: "FastSpring",
  stripe: "Stripe",
  bluesnap: "BlueSnap",
  apple: "App Store",
  google: "Google Play",
};

export interface BillingSubscription {
  id: string;
  status: string;
  product: string | null;
  /**
   * What the customer is charged each period. Gross (tax included) when the
   * provider gives a gross figure — FastSpring's `subtotal` excludes tax, which
   * is why a €158.77 subscription produces a €188.94 charge at 19% VAT.
   */
  amount: number | null;
  /** Pre-discount list price, when it differs from `amount`. */
  listAmount: number | null;
  currency: string | null;
  interval: string | null;
  startDate: string | null;
  nextChargeDate: string | null;
  cancelledDate: string | null;
  autoRenew: boolean | null;
  /** Which of the account's addresses this was found under. */
  sourceEmail: string;
}

export type BillingEventType = "charge" | "refund" | "cancel" | "other";

export interface BillingEvent {
  id: string;
  type: BillingEventType;
  date: string | null;
  amount: number | null;
  currency: string | null;
  status: string | null;
  reference: string | null;
  /** Receipt/invoice the support agent can open or send to the customer. */
  invoiceUrl: string | null;
  /** e.g. "visa •••• 4242" — enough to match a customer's bank statement. */
  paymentMethod: string | null;
  items: string[];
  /** The subscription this paid for, when the provider says. */
  subscriptionId: string | null;
  sourceEmail: string;
}

/**
 * One provider's view of a user. `available: false` means we could not ask the
 * provider (no credentials, provider has no API) — distinct from "asked, and
 * this user is not a customer there", which is available with empty lists.
 *
 * A user who changed their email usually keeps paying under the old address, so
 * every address the account has ever used is queried and the results merged;
 * `customers` records which address produced which provider account.
 */
export interface BillingSnapshot {
  provider: BillingProvider;
  available: boolean;
  unavailableReason: string | null;
  customers: { email: string; customerId: string }[];
  subscriptions: BillingSubscription[];
  events: BillingEvent[];
}

// ── Operation inputs ────────────────────────────────────────────────────

/** Product plans the console can assign. Values are upstream plan ids. */
export const PRODUCT_PLANS = [
  { label: "Basic", value: 1 },
  { label: "Ultimate", value: 5 },
  { label: "Premium", value: 6 },
  { label: "TR+", value: 7 },
  { label: "Nasdaq Pro", value: 5005 },
] as const;

export const ADDON_GROUPS = [
  { label: "Smart Investor", value: 1 },
  { label: "Download Excel", value: 2 },
  { label: "Download Excel Free", value: 3 },
  { label: "Dividend", value: 4 },
  { label: "Growth", value: 5 },
  { label: "Smart Value", value: 7 },
] as const;

export const ORIGIN_SUBDOMAINS = [
  { label: "Other", value: 0 },
  { label: "www", value: 1 },
  { label: "Canada", value: 2 },
  { label: "UK", value: 3 },
] as const;

/** Audit reasons offered for plan/add-on changes. "other" means free text. */
export const ASSIGN_REASONS = [
  "TipRanks Employee/Writer",
  "Potential B2B customer",
  "The customer did not pay the last invoice",
  "Compensation upgrade",
  "Customer interview incentive",
  "The customer asked to downgrade from Ultimate to Premium",
  "User deleted their account, but still has an active payment",
  "Double-charged, permissions reapplied",
  "Customer asked to cancel subscription",
  "User did not receive product permission after successful payment.",
] as const;

export interface AssignSubscriptionRequest {
  email: string;
  productPlan: number;
  expiryDate: string;
  reason: string;
}

export interface CreateSiteUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  productPlan: number;
  originSubDomain: number;
  expiryDate: string;
  reason: string;
}

export interface AddOnRequest {
  email: string;
  addOnGroup: number;
  expiryDate?: string;
  reason: string;
}

export interface ChangeEmailRequest {
  email: string;
  newEmail: string;
  /**
   * Also rewrite the address on the matching FastSpring/Stripe accounts.
   * Defaults to false: changing TipRanks alone leaves receipts and dunning
   * mail going to the old address, but rewriting a provider account is a write
   * to a payment system and must be a deliberate act.
   */
  updateProviders?: boolean;
}

/** Outcome of one provider account's email rewrite. */
export interface ProviderEmailUpdate {
  provider: BillingProvider;
  customerId: string;
  fromEmail: string;
  ok: boolean;
  error: string | null;
}

export interface ChangeEmailResponse {
  success: true;
  message: string;
  /** Empty when the operator did not opt in. */
  providerUpdates: ProviderEmailUpdate[];
}

export interface UpdateUserPasswordRequest {
  email: string;
  password: string;
}

export interface AddPortfolioRequest {
  email: string;
}

// ── Responses ───────────────────────────────────────────────────────────

export interface UserLookupResponse {
  account: UserAccount;
  /** What was searched for — may be an address the user has since replaced. */
  requestedEmail: string;
  /** True when `requestedEmail` is not the account's current address. */
  isAlias: boolean;
}

/** Current address plus every address the account has used, oldest last. */
export function knownEmails(account: UserAccount): string[] {
  const all = [account.email, ...account.emailHistory.map((e) => e.email)];
  return [...new Set(all.map((e) => e.trim().toLowerCase()).filter(Boolean))];
}

/** What an order reference or provider id resolved to. */
export interface ResolveReferenceResponse {
  email: string;
  /** How it was found, e.g. "FastSpring order TIPRANKS260820-9918-94127". */
  via: string;
}

export interface UserBillingResponse {
  snapshots: BillingSnapshot[];
  /** Current address plus any the account previously used. */
  emailsQueried: string[];
}

/** One thing an operator did to this user, from the admin panel's own log. */
export interface UserActivityEntry {
  at: string;
  /** The admin who did it. */
  user: string;
  action: string;
  target: string;
  result: "ok" | "error" | "started";
  reason: string | null;
  detail: string | null;
  params: Record<string, unknown> | null;
}

export interface UserActivityResponse {
  entries: UserActivityEntry[];
}

/** Human labels for the audited actions. */
export const ACTIVITY_LABELS: Record<string, string> = {
  "user:assign-subscription": "Assigned subscription",
  "user:add-addon": "Added add-on",
  "user:remove-addon": "Removed add-on",
  "user:create": "Created user",
  "user:change-email": "Changed email",
  "user:verify-email": "Verified email",
  "user:stop-emails": "Stopped all emails",
  "user:set-password": "Set password",
  "user:activate-terms": "Activated terms of use",
  "user:add-portfolio": "Added Nasdaq portfolio",
  "user:delete-data": "Deleted user data",
};

export interface UserOperationResponse {
  success: true;
  message: string;
}
